import "./_OffreOperationCommerciale.scss";

import cx from "classnames";
import Decimal from "decimal.js";
import debounce from "lodash.debounce";
import {ReactElement, useContext, useEffect, useState} from "react";
import React from "react";
import {useWatch} from "react-hook-form";
import {Controller} from "react-hook-form";

import BlocLabel from "@/components/BlocLabel/BlocLabel";
import Button from "@/components/Button/Button";
import {InputFile} from "@/components/InputFile/InputFile";
import InputLabel from "@/components/InputLabel/InputLabel";
import Loader from "@/components/Loader/loader";
import {Modale} from "@/components/Modale/Modale";
import MultiSelect from "@/components/MultiSelect/MultiSelect";
import SelectCustom from "@/components/SelectCustom/SelectCustom";
import {HookFormContext} from "@/context/HookFormContext";
import {
  getAllPrix,
  getOptionFormat,
  getPrixPalier,
  hasRepiquage,
} from "@/function/operationCommercialeToole";
import {OptionNameValue} from "@/ts/interface/formulaire";
import {Magasin} from "@/ts/interface/magasin";
import {
  FormItem,
  FormSelectedMagasin,
  OffreOperationCommerciale as Offre,
} from "@/ts/interface/operationCommerciale";

import {FileAndLinkToDL} from "../FileAndLinkToDL/FileAndLinkToDL";
import GrilleTarifaireOperationCommercial from "../GrilleTarifaireOperationCommerciale/GrilleTarifaireOperationCommercial";
import {PersonnalisationOffreAuMagasin} from "../PersonnalisationOffreAuMagasin/PersonnalisationOffreAuMagasin";

type ParamasModalType = {
  isOpen: boolean;
  title: string;
  content: string | ReactElement;
};

function OffreOperationCommerciale({
  offre,
  magasins,
}: {
  offre: Offre;
  magasins: Magasin[];
}): ReactElement {
  const context = useContext(HookFormContext);

  if (!context) {
    throw new Error("HookFormContext is not provided.");
  }

  const {
    setValue,
    getValues,
    watch,
    register,
    control,
    formState: {errors},
    printError,
    atLeastOneFilled,
    unregister,
  } = context;

  const [commandeIsCommun, setCommandeIsCommun] = useState<boolean>(
    getValues(`offres.O${offre.offre_id.toString()}.commun`) !== undefined
      ? getValues(`offres.O${offre.offre_id.toString()}.commun`)
      : true,
  );

  useEffect(() => {
    if (commandeIsCommun) {
      setValue(`offres.O${offre.offre_id.toString()}.commun`, true);
    } else {
      setValue(`offres.O${offre.offre_id.toString()}.commun`, false);
    }
  }, [commandeIsCommun]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars

  const defaultModalValue: ParamasModalType = {
    isOpen: false,
    title: "",
    content: "",
  };
  const [modale, setModale] = useState<ParamasModalType>(defaultModalValue);
  const rerenderLabel = `offres.O${offre.offre_id}.rerender`;
  // On regarde le champ spécifique de l'item
  const rerenderValues = useWatch({
    control,
    name: rerenderLabel, // <- ce nom dépend de la structure exacte
  });
  const selectedMagasin = watch(
    `offres.O${offre.offre_id.toString()}.selected_magasins`,
  ) as FormSelectedMagasin[];
  // met a jours les valeur par default
  const handleUpdateDefaultValues = debounce((): void => {
    const defaultValues: {items: Record<string, FormItem>} = getValues(
      `offres.O${offre.offre_id.toString()}.default`,
    );

    const copyDefaultValue: {items: Record<string, FormItem>} =
      structuredClone(defaultValues);
    selectedMagasin?.forEach((mag: any) => {
      offre.items.forEach(item => {
        // si l'item existe conserve le préstataire de diffusion et / ou les fichiers de livraisons
        if (copyDefaultValue.items[`I${item.item_id}`]) {
          // recupére les prestataire de distribution en cours
          const currentPrestataireDistribution = getValues(
            `offres.O${offre.offre_id.toString()}.magasins.M${
              mag?.value
            }.items.I${item.item_id}.prestataire_distribution`,
          );

          copyDefaultValue.items[`I${item.item_id}`] = {
            ...copyDefaultValue.items[`I${item.item_id}`],
            prestataire_distribution: currentPrestataireDistribution,
          };
          // recupére les livraisons en cours
          const currentLivraison = getValues(
            `offres.O${offre.offre_id.toString()}.magasins.M${
              mag?.value
            }.items.I${item.item_id}.livraisons`,
          );

          // Pour toutes les livraisons si un fichier est renseigné on le conserve.
          Object.entries(
            copyDefaultValue.items[`I${item.item_id}`].livraisons,
          ).forEach(([key]) => {
            if (currentLivraison?.[key]?.fichiers) {
              copyDefaultValue.items[`I${item.item_id}`].livraisons[
                key
              ].fichiers = currentLivraison[key]?.fichiers;
            }
          });

          copyDefaultValue.items[`I${item.item_id}`] = {
            ...copyDefaultValue.items[`I${item.item_id}`],
          };
        }
      });
      // ajoute la valeur par défaut pour chaque magasin avec les bon fichier / préstataire de distribution.
      setValue(`offres.O${offre.offre_id.toString()}.magasins.M${mag?.value}`, {
        ...copyDefaultValue,
      });
    });
  }, 400);

  useEffect(() => {
    // Ajoute les valeur par défaut pour chaque item de l'offre
    // En fonction des information déjà renseigner comme les quanités de livraison
    setValue(`offres.O${offre.offre_id.toString()}.forfait`, offre.forfait);
    setValue(`offres.O${offre.offre_id.toString()}.nom`, offre.nom);

    offre.items.forEach(item => {
      const itemName = `offres.O${offre.offre_id.toString()}.default.items.I${
        item.item_id
      }`;
      setValue(`${itemName}.nom`, item.nom);
      setValue(`${itemName}.forfait`, item?.forfait);

      // Récupère la sommes des quantités de toutes les livraisons
      // Permet de vérifier le palier en cours.
      const currentlivraison = getValues(`${itemName}.livraisons`) || {};
      let currentQuantite = 0;

      for (const key in currentlivraison) {
        currentQuantite += currentlivraison[key]?.quantite || 0;
      }

      // on multiplie les livraison par le nombre de magasin pour avoir les quantités réel commandées
      currentQuantite = currentQuantite * (selectedMagasin?.length || 1);

      const formatName = `${itemName}.format`;
      let formatPrixPalier = getValues(`${formatName}.complementData`);
      // si le complementData est vide, on récupère les prix du format pour les mettre dans le complementData
      if (formatPrixPalier?.length === 0) {
        const formatIndex = item.formats.findIndex(form => {
          return form.item_format_id === getValues(`${formatName}.value`);
        });
        console.log("formatIndex", formatIndex);
        if (formatIndex >= 0) {
          setValue(
            `${formatName}.complementData`,
            item.formats[formatIndex].prix,
          );
        }
        formatPrixPalier = item.formats[formatIndex].prix;
      }
      // ajoute le prix unitaire des formats
      const formatPrixUnitairePalier = getPrixPalier(
        formatPrixPalier,
        currentQuantite <= 0 ? 1 : currentQuantite,
      );
      if (formatPrixUnitairePalier) {
        setValue(
          `${formatName}.prix_unitaire`,
          getPrixPalier(
            formatPrixPalier,
            currentQuantite <= 0 ? 1 : currentQuantite,
          ),
        );
      }
      const {options} = getOptionFormat(
        item,
        currentQuantite <= 0 ? 1 : currentQuantite,
      );
      options.forEach((_option, index) => {
        const optionPrixPalier = getValues(
          `${itemName}.options[${index}].complementData`,
        );

        if (optionPrixPalier) {
          setValue(
            `${itemName}.options[${index}].prix_unitaire`,
            getPrixPalier(
              optionPrixPalier,
              currentQuantite <= 0 ? 1 : currentQuantite,
            ),
          );
        }
      });
      item.livraisons.forEach(livraison => {
        setValue(
          `${itemName}.livraisons.L${livraison.livraison_id}.livraison_label`,
          livraison.type,
        );
        setValue(
          `${itemName}.livraisons.L${livraison.livraison_id}.prixPalier`,
          livraison.prix,
        );
      });
    });
    // met a jours les valeur par default quand on a une récupération de commande enregistré
    if (
      (rerenderValues !== undefined && commandeIsCommun === true) ||
      commandeIsCommun === true
    ) {
      console.log("lalalal");
      handleUpdateDefaultValues();
    }
    // setRerender(prev => prev + 1);
  }, [rerenderValues]);

  useEffect(() => {
    if (selectedMagasin?.length === 0) {
      setValue(`offres.O${offre.offre_id.toString()}.magasins`, undefined);
    } else if (commandeIsCommun) {
      handleUpdateDefaultValues();
    }
  }, [selectedMagasin]);
  // permet de renseigner automatiquement les valeurs lors de la sélection d'un magasin
  // Utiliser quand un seul format et une seul quantité de livraison est possible.

  //PROVISOIREMENT DESACTIVER car fait bug en mettant un prix a 0
  // useEffect(() => {
  //   if (
  //     offre.items.length === 1 &&
  //     offre.items[0].formats.length === 1 &&
  //     offre.items[0].livraisons.length === 1 &&
  //     commandeIsCommun
  //   ) {
  //     const livraisonQuantite = getValues(
  //       `offres.O${offre.offre_id.toString()}.default.items.I${offre.items[0].item_id}.livraisons.L${offre.items[0].livraisons[0].livraison_id}.quantite`,
  //     );
  //     // met les valeurs par défaut uniquement lors du premier magasin séléctionné
  //     if (selectedMagasin?.length > 0 && !livraisonQuantite) {
  //       setValue(
  //         `offres.O${offre.offre_id.toString()}.default.items.I${offre.items[0].item_id}.format`,
  //         {
  //           value: offre.items[0].formats[0].item_format_id,
  //           libelle: offre.items[0].formats[0].nom,
  //           complementData: offre.items[0].formats[0].prix,
  //         },
  //       );
  //       setValue(
  //         `offres.O${offre.offre_id.toString()}.default.items.I${offre.items[0].item_id}.livraisons.L${offre.items[0].livraisons[0].livraison_id}.quantite`,
  //         offre.items[0].livraisons[0].qte_min || 1,
  //       );
  //     }
  //     // reset les valeurs quand on déselectionne le dernier magasin.
  //     else if (selectedMagasin?.length === 0) {
  //       setValue(
  //         `offres.O${offre.offre_id.toString()}.default.items.I${offre.items[0].item_id}.format`,
  //         undefined,
  //       );
  //       setValue(
  //         `offres.O${offre.offre_id.toString()}.default.items.I${offre.items[0].item_id}.livraisons.L${offre.items[0].livraisons[0].livraison_id}.quantite`,
  //         "",
  //       );
  //     }
  //   }
  // }, [selectedMagasin]);
  // Récupère tout les nom des item
  const allItemName: string[] = [];
  offre.items.map(itemValue => {
    allItemName.push(
      `offres.O${offre.offre_id.toString()}.default.items.I${
        itemValue.item_id
      }.format.value`,
    );
  });
  const allItems = offre.items.map((item, index) => {
    const itemName = `offres.O${offre.offre_id.toString()}.default.items.I${
      item.item_id
    }`;
    // Récupère la sommes des quantités de toutes les livraisons
    // Permet de vérifier le palier en cours.
    const currentlivraison = getValues(`${itemName}.livraisons`) || {};
    let currentQuantite = 0;

    for (const key in currentlivraison) {
      currentQuantite += currentlivraison[key]?.quantite || 0;
    }

    // on multiplie les livraison par le nombre de magasin pour avoir les quantités réel commandées
    currentQuantite = currentQuantite * (selectedMagasin?.length || 1);
    const {options, formats} = getOptionFormat(
      item,
      currentQuantite <= 0 ? 1 : currentQuantite,
    );

    const formatName = `${itemName}.format`;
    const formatValue = getValues(`${formatName}.value`);

    const prixUnitaireFormat = getValues(`${formatName}.prix_unitaire`);

    // Récupère tout les nom de livraison pour vérifier si au moins une livraison est remplie
    const allLivraisonName: string[] = [];
    item.livraisons.map(livraison => {
      allLivraisonName.push(
        `${itemName}.livraisons.L${livraison.livraison_id}.quantite`,
      );
    });
    const livraisonIsRequire =
      selectedMagasin?.length > 0 && formatValue
        ? atLeastOneFilled(
            allLivraisonName,
            getValues,
            "Mettez une quantité sur au moins une livraison",
          )
        : false;

    const itemFormatIsRequire =
      selectedMagasin?.length > 0
        ? atLeastOneFilled(
            allItemName,
            getValues,
            allItemName.length > 1
              ? "Choisissez au moins 1 item"
              : "Choisissez le format",
          )
        : false;

    return (
      <div className="offreItems" key={item.item_id}>
        {/* Afficher le message d'erreur */}
        {index === 0 ? printError(`${formatName}.message`, errors) : null}
        <div className="offreFormat">
          <div className="offreFormat-select">
            <Controller
              name={formatName}
              control={control}
              rules={{
                // Permet plusieurs règles de validation
                validate: {
                  hasValue: selected =>
                    itemFormatIsRequire && commandeIsCommun
                      ? selected?.value || itemFormatIsRequire
                      : true,
                },
              }}
              render={({field}) => {
                return (
                  <>
                    <SelectCustom
                      key={field.name}
                      defaultValue=""
                      label={item.nom.toUpperCase()}
                      ref={field.ref}
                      name={field.name}
                      placeholder="-- Selectionner un format --"
                      disabled={!commandeIsCommun}
                      options={formats}
                      // eslint-disable-next-line @typescript-eslint/no-unused-vars
                      handleSelect={(value, libelle, complementData) => {
                        const rerender = getValues(rerenderLabel) || 0;
                        setValue(rerenderLabel, rerender + 1);
                        // Déclenche le changement du champ via field.onChange
                        field.onChange({
                          libelle: libelle,
                          value,
                          complementData: complementData, // ou autre si tu en as
                        });
                      }}
                      onTrash={() => {
                        field.onChange(undefined);
                        // reset les options
                        setValue(`${itemName}.options`, []);
                        const livraisons = getValues(`${itemName}.livraisons`);
                        //reset les livraisons
                        Object.entries(livraisons)?.forEach(([key]) => {
                          setValue(
                            `${itemName}.livraisons.${key}.quantite`,
                            "",
                          );
                        });
                        const rerender = getValues(rerenderLabel) || 0;
                        setValue(rerenderLabel, rerender + 1);
                      }}
                      setValue={setValue}
                      watch={watch}
                      complementLabel={
                        prixUnitaireFormat
                          ? `(+${new Decimal(prixUnitaireFormat).toFixed(
                              2,
                            )}€/u)`
                          : ""
                      }
                    />
                  </>
                );
              }}
            />
            {item?.forfait && parseFloat(item.forfait) > 0 ? (
              <p className="offreOperation-prixForfait">
                {`Prix forfait ${item.nom} : ${new Decimal(item.forfait)
                  .toDecimalPlaces(2, Decimal.ROUND_HALF_UP)
                  .toFixed(2)} €`}{" "}
                <br />
                <span>
                  (S'applique une seule fois, quelle que soit la quantité
                  commandée)
                </span>
              </p>
            ) : null}
          </div>

          <div className="offreFormat-livraison">
            {printError(
              `${itemName}.livraisons.L${item?.livraisons[0]?.livraison_id}.quantite.message`,
              errors,
            )}
            {item.livraisons.map(livraison => {
              const name = `${itemName}.livraisons.L${livraison.livraison_id}.quantite`;
              const currentLivraison = useWatch({
                control,
                name: `${itemName}.livraisons`,
              });
              let currentTotalQuantite = 0;
              for (const key in currentLivraison) {
                currentTotalQuantite += currentLivraison[key].quantite || 0;
              }

              const {
                TotalPrixItemLivraison,
                prixUnitaireTotal,
                prixUnitaireLivraison,
              } = getAllPrix(
                getValues(`${formatName}`),
                livraison.prix,
                getValues(`${itemName}.options`),
                currentTotalQuantite,
                getValues(name),
              );

              const resetPrestataireDistribution = (): void => {
                selectedMagasin?.forEach(mag => {
                  if (
                    getValues(
                      `offres.O${offre.offre_id.toString()}.magasins.M${
                        mag.value
                      }.items.I${item.item_id}.prestataire_distribution`,
                    )
                  ) {
                    unregister(
                      `offres.O${offre.offre_id.toString()}.magasins.M${
                        mag.value
                      }.items.I${item.item_id}.prestataire_distribution`,
                    );
                  }
                });
              };
              // permet de savoir si on doit avoir un préstataire de distribution ou non.
              const havePrestataireDistribution =
                livraison.type === "BAL" && getValues(name) && commandeIsCommun;
              if (
                livraison.type === "BAL" &&
                !getValues(name) &&
                commandeIsCommun
              ) {
                resetPrestataireDistribution();
              }
              // récupère les préstataires de distribution
              const prestataireDistribution = selectedMagasin?.map(mag => {
                const finedIndexMag = magasins.findIndex(
                  m => m.magasin_id === mag.value,
                );
                const prestataireDistribution: OptionNameValue[] =
                  magasins?.[finedIndexMag]?.prestataire_distribution?.map(
                    prestataire => {
                      return {
                        libelle: prestataire.nom,
                        value: prestataire.prestataire_distribution_id,
                      };
                    },
                  ) || [];

                return (
                  <div
                    key={mag.value}
                    className="offreFormat-livraison--prestataire">
                    {printError(
                      `offres.O${offre.offre_id.toString()}.magasins.M${
                        mag.value
                      }.items.I${
                        item.item_id
                      }.prestataire_distribution.message`,
                      errors,
                    )}
                    <Controller
                      name={`offres.O${offre.offre_id.toString()}.magasins.M${
                        mag.value
                      }.items.I${item.item_id}.prestataire_distribution`}
                      control={control}
                      rules={{
                        required: havePrestataireDistribution
                          ? "Choisissez un prestataire de distribution"
                          : false,
                      }}
                      render={({field}) => (
                        <SelectCustom
                          name={field.name}
                          defaultValue=""
                          ref={field.ref}
                          label={mag.libelle}
                          placeholder="-- Choix du prestataire --"
                          disabled={!commandeIsCommun}
                          options={prestataireDistribution}
                          // eslint-disable-next-line @typescript-eslint/no-unused-vars
                          handleSelect={(value, libelle) => {
                            // Déclenche le changement du champ via field.onChange
                            field.onChange({
                              libelle: libelle,
                              value,
                              complementData: null, // ou autre si tu en as
                            });
                          }}
                          setValue={setValue}
                          watch={watch}
                        />
                      )}
                    />
                  </div>
                );
              });
              const haveRoutage =
                livraison.type === "Routage" &&
                getValues(name) &&
                commandeIsCommun;
              const UploadeRoutageFile = selectedMagasin?.map(mag => {
                const isRequire = (): boolean | string => {
                  if (haveRoutage) {
                    const nbSelectedMag = selectedMagasin.length;
                    let nbFile = 0;
                    // eslint-disable-next-line @typescript-eslint/no-unused-vars
                    selectedMagasin?.forEach(magasin => {
                      const fichier = getValues(
                        `offres.O${offre.offre_id.toString()}.magasins.M${
                          magasin.value
                        }.items.I${item.item_id}.livraisons.L${
                          livraison.livraison_id
                        }.fichiers[0].fichier`,
                      );
                      if (fichier?.length) {
                        nbFile += 1;
                      }
                    });
                    if (nbFile === 1 || nbFile === nbSelectedMag) {
                      return false;
                    }
                  }
                  return "Ajouter 1 fichier de routage ou autant qu'il y a de magasin";
                };
                return (
                  <div key={mag.value}>
                    <InputFile
                      control={control}
                      name={`offres.O${offre.offre_id.toString()}.magasins.M${
                        mag.value
                      }.items.I${item.item_id}.livraisons.L${
                        livraison.livraison_id
                      }.fichiers[0].fichier`}
                      label={mag.libelle}
                      isRequire={isRequire()}
                      handleDeleteFile={() => {
                        setValue(
                          `offres.O${offre.offre_id.toString()}.magasins.M${
                            mag.value
                          }.items.I${item.item_id}.livraisons.L${
                            livraison.livraison_id
                          }.fichiers[0].fichierId`,
                          undefined,
                        );
                      }}
                    />
                  </div>
                );
              });

              const placeholderValue = (): string => {
                if (
                  livraison?.qte_min &&
                  livraison?.qte_max &&
                  livraison?.qte_max < 9999999
                ) {
                  return `${livraison?.qte_min} - ${livraison?.qte_max}`;
                } else {
                  return `Min(${livraison?.qte_min | 1})`;
                }
              };
              return (
                <React.Fragment key={name}>
                  <InputLabel
                    id={`${livraison.type} ${item.item_id}`}
                    label={livraison.type}
                    disabled={!commandeIsCommun || !formatValue}
                    min={livraison?.qte_min}
                    max={livraison?.qte_max || 9999999}
                    placeholder={placeholderValue()}
                    type="number"
                    onKeyDown={e => {
                      if (e.key === "Enter") e.preventDefault();
                    }}
                    complementLibelle={
                      prixUnitaireLivraison
                        ? `(+${new Decimal(prixUnitaireLivraison).toFixed(
                            2,
                          )}€/u)`
                        : ""
                    }
                    complementLabel={
                      parseFloat(TotalPrixItemLivraison) > 0
                        ? `Total : ${TotalPrixItemLivraison} €/Magasin Soit :  ${prixUnitaireTotal.toString()}€ /u`
                        : ""
                    }
                    {...register(name, {
                      onChange: () => {
                        setValue(
                          rerenderLabel,
                          (getValues(rerenderLabel) || 0) + 1,
                        );
                      },
                      valueAsNumber: true,
                      required: commandeIsCommun ? livraisonIsRequire : false,
                    })}
                  />
                  {havePrestataireDistribution ? (
                    <div id="prestataireDistribution">
                      <h3>Prestataire de diffusion</h3>
                      {prestataireDistribution}
                    </div>
                  ) : null}
                  {haveRoutage ? (
                    <div id="routage">
                      <h3>Import du fichier</h3>
                      {selectedMagasin?.reduce<React.ReactNode | null>(
                        (acc, mag) => {
                          if (acc) return acc; // Si un message a déjà été trouvé, on le retourne
                          return printError(
                            `offres.O${offre.offre_id.toString()}.magasins.M${
                              mag.value
                            }.items.I${item.item_id}.livraisons.L${
                              livraison.livraison_id
                            }.fichiers.0.fichier.message`,
                            errors,
                          );
                        },
                        null,
                      )}
                      {UploadeRoutageFile}
                    </div>
                  ) : null}
                </React.Fragment>
              );
            })}
          </div>
        </div>
        {options?.length > 0 ? (
          <div className="offreOptions">
            <MultiSelect
              label="OPTION"
              options={options}
              name={`${itemName}.options`}
              // eslint-disable-next-line @typescript-eslint/no-unused-vars
              onClick={_ => {
                const rerender = getValues(rerenderLabel) || 0;
                setValue(rerenderLabel, rerender + 1);
              }}
              disabled={!commandeIsCommun || !formatValue}
              maxOptionSelect={item.limite_option || 9999999}
              watch={watch}
              nbLigne={2}
              hasBorder={false}
              hasButtonAllSelect={false}
              uniqueKey={`${offre.offre_id} ${item.item_id}`}
              control={control}
              setValue={setValue}
            />
          </div>
        ) : null}
      </div>
    );
  });
  const magasinsComponent = magasins
    ?.filter(mag => {
      const findINdex = selectedMagasin?.findIndex(
        selectedMag => selectedMag.value === mag.magasin_id,
      );
      return findINdex !== undefined && findINdex > -1;
    })
    .map(mag => {
      return (
        <PersonnalisationOffreAuMagasin
          magasin={mag}
          offre={offre}
          key={mag.magasin_id}
        />
      );
    });
  const haveItem = allItems.length > 0;

  return (
    <div className="offreOperation">
      <BlocLabel label={offre.nom} />
      <div className="offreOperation-header">
        {offre.images?.[0]?.url ? (
          <div className="offreOperation-header--image">
            <img
              src={offre.images?.[0]?.url}
              alt={`illustration de l'offre ${offre.nom}`}
            />
          </div>
        ) : null}
        <div className="offreOperation-header--description description-text">
          <div dangerouslySetInnerHTML={{__html: offre?.description}}></div>
          <div className="offreOperation-header--fichiers">
            <FileAndLinkToDL fichiers={offre?.fichiers} liens={offre?.liens} />
          </div>

          <Button
            label="Consulter la grille tarifaire"
            className="offreOperation-header--grilleTarifaire"
            onClick={() =>
              setModale({
                isOpen: true,
                title: `Grille tarifaire ${offre.nom}`,
                content: <GrilleTarifaireOperationCommercial offre={offre} />,
              })
            }
            variant="secondary"
          />
        </div>
      </div>
      {offre?.forfait && parseFloat(offre.forfait) > 0 ? (
        <p className="offreOperation-prixForfait">
          {`Prix forfait : ${new Decimal(offre.forfait).toString()} € `}
          <br />
          <span>
            (S'applique une seule fois, quelle que soit la quantité commandée)
          </span>
        </p>
      ) : null}
      <div className="offreOperation-parametrage">
        <div
          className={cx(
            "offreOperation-parametrage--left",
            haveItem ? "" : "offreOperation-noItem",
          )}>
          {magasins?.length > 0 ? (
            <MultiSelect
              name={`offres.O${offre.offre_id.toString()}.selected_magasins`}
              options={magasins?.map(mag => {
                const canPlaceOrder =
                  mag.statut_commande === "Validée" ||
                  mag.statut_commande === "Refusée";
                const adresse = mag.adresse
                  ? `${mag.adresse.value} ${mag.adresse.postalCode} ${mag.adresse.city}`
                  : null;
                return {
                  libelle: mag.raison_sociale,
                  value: mag.magasin_id || "",
                  defaultChecked: false,
                  isDisabled: canPlaceOrder,
                  tooltip: canPlaceOrder
                    ? `Ce magasin à déjà ${
                        mag.statut_commande === "Refusée"
                          ? "Refusée"
                          : "Commandée"
                      } cette opération`
                    : adresse
                    ? adresse
                    : undefined,
                };
              })}
              nbLigne={haveItem ? 1 : 4}
              label="Point de vente partiticipant"
              needMin1Check={false}
              uniqueKey={`offres.O${offre.offre_id.toString()}.selected_magasins`}
              isRequired={
                atLeastOneFilled(allItemName, getValues)
                  ? false
                  : {value: true, message: "Choisissez au moins 1 magasin"}
              }
              onClick={(isChecked, selectedValue) => {
                // permet de donner les valeurs par defaut au magasin
                if (isChecked) {
                  setValue(
                    `offres.O${offre.offre_id.toString()}.magasins.M${selectedValue}`,
                    getValues(`offres.O${offre.offre_id.toString()}.default`),
                  );
                } else {
                  unregister(
                    `offres.O${offre.offre_id.toString()}.magasins.M${selectedValue}`,
                  );

                  // si avant de décocher le magasin il n'en restait que 1.
                  // alors on repasse en paramétrage commun.
                  if (selectedMagasin?.length === 1) {
                    setCommandeIsCommun(true);
                  }
                }
                const rerender = getValues(rerenderLabel) || 0;
                setValue(rerenderLabel, rerender + 1);
              }}
              control={control}
              setValue={setValue}
              watch={watch}
            />
          ) : (
            <Loader />
          )}

          {hasRepiquage(offre) ? (
            <Controller
              name={`offres.O${offre.offre_id.toString()}.repiquage`}
              control={control}
              rules={{
                required:
                  magasinsComponent?.length > 0
                    ? "Le repiquage est requis"
                    : false,
              }}
              render={({field, fieldState}) => (
                <>
                  <SelectCustom
                    defaultValue=""
                    label={"Repiquage"}
                    placeholder="-- Selectionner un repiquage --"
                    ref={field.ref} // pour le focus de l'erreur de react hook form
                    options={[
                      {
                        libelle: "Individuel",
                        value: "Individuel",
                      },
                      {libelle: "Commun", value: "Commun"},
                    ]}
                    handleSelect={value => {
                      // Déclenche le changement du champ via field.onChange
                      field.onChange({
                        libelle: value,
                        value,
                        complementData: null, // ou autre si tu en as
                      });

                      const rerender = getValues(rerenderLabel) || 0;
                      setValue(rerenderLabel, rerender + 1);
                    }}
                    name={field.name}
                    setValue={setValue}
                    watch={watch}
                  />
                  {fieldState?.error && (
                    <p className="form-error">{fieldState.error.message}</p>
                  )}
                </>
              )}
            />
          ) : null}
        </div>
        {allItems?.length > 0 ? (
          <div className="offreOperation-parametrage--right">
            <h3>Choix pour tout mes points de vente</h3>

            {allItems}
          </div>
        ) : null}
      </div>
      {(magasinsComponent?.length || 0) > 0 && offre?.items?.length ? (
        commandeIsCommun ? (
          <>
            <Button
              label="Personnaliser l'engagement aux points de vente"
              className="offreOperation-btn--parametrage"
              variant="secondary"
              onClick={() => {
                // permet de bien mettre les valeur par defaut dans la personnalisation
                const rerender = getValues(rerenderLabel) || 0;
                setValue(rerenderLabel, rerender + 1);
                setCommandeIsCommun(false);
              }}
            />
          </>
        ) : (
          <>
            <Button
              label="Retour paramétrage commun"
              className="offreOperation-btn--parametrage"
              variant="primary"
              onClick={() => {
                // permet de bien mettre les valeur par defaut dans la personnalisation
                const rerender = getValues(rerenderLabel) || 0;
                setValue(rerenderLabel, rerender + 1);
                setCommandeIsCommun(true);
              }}
            />
            <div className="offreOperation-parametrage--personnalisation">
              {magasinsComponent}
            </div>
          </>
        )
      ) : null}
      <Modale
        isOpen={modale.isOpen}
        title={modale.title}
        children={modale.content}
        close={() => {
          setModale(defaultModalValue);
        }}
      />
    </div>
  );
}

export default OffreOperationCommerciale;
